import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// ─── Types ───

export interface ModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Modal title rendered in the header */
  title?: string;
  /** Optional description below the title */
  description?: string;
  /** Modal max width — default 480px */
  maxWidth?: number | string;
  /** Modal body content */
  children: ReactNode;
  /** Optional sticky footer content */
  footer?: ReactNode;
  /** Close on backdrop click — default true */
  closeOnBackdrop?: boolean;
  /** Close on Escape key — default true */
  closeOnEsc?: boolean;
  /** Show close (X) button — default true */
  showCloseButton?: boolean;
  /** Style applied to the portal root — use to forward CSS custom properties (e.g. brand tokens) */
  portalStyle?: CSSProperties;
}

// ─── Constants ───

const MODAL_MAX_WIDTH_DEFAULT = 480;
const TRANSITION_MS = 200;

// ─── Close Icon (X) — inline SVG ───

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Focus Trap Hook ───

function useFocusTrap(containerRef: React.RefObject<HTMLDivElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const firstFocusable = container.querySelector<HTMLElement>(focusableSelector);
    firstFocusable?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusables = container!.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, containerRef]);
}

// ─── Body Scroll Lock ───

function useBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

// ─── Modal Component ───

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  maxWidth = MODAL_MAX_WIDTH_DEFAULT,
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEsc = true,
  showCloseButton = true,
  portalStyle,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Esc key handler
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onOpenChange(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEsc, onOpenChange]);

  useFocusTrap(modalRef, open);
  useBodyScrollLock(open);

  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdrop) onOpenChange(false);
  }, [closeOnBackdrop, onOpenChange]);

  const resolvedMaxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

  if (!mounted) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: visible ? 'auto' : 'none',
        ...portalStyle,
      }}
    >
      {/* Backdrop / Scrim */}
      <div
        onClick={handleBackdropClick}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          opacity: visible ? 1 : 0,
          transition: `opacity ${TRANSITION_MS}ms ease-out`,
        }}
      />

      {/* Modal Panel */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: resolvedMaxWidth,
          maxHeight: '90vh',
          margin: '0 var(--sp-4, 16px)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-surface, #ffffff)',
          borderRadius: 'var(--radius-lg, 14px)',
          boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1)' : 'scale(0.95)',
          transition: `opacity ${TRANSITION_MS}ms ease-out, transform ${TRANSITION_MS}ms ease-out`,
          overflow: 'hidden',
          fontFamily: 'var(--font-family, Inter, sans-serif)',
        }}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            style={{
              flexShrink: 0,
              borderBottom: '1px solid var(--border, #e5e7eb)',
              padding: 'var(--sp-4, 16px) var(--sp-6, 24px)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 'var(--sp-4, 16px)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1, 4px)' }}>
              {title && (
                <h2
                  id={titleId}
                  style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    lineHeight: '28px',
                    letterSpacing: '-0.45px',
                    color: 'var(--text, #111827)',
                  }}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id={descId}
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-sm, 12px)',
                    lineHeight: 'var(--line-height-sm, 16px)',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {description}
                </p>
              )}
            </div>

            {showCloseButton && (
              <button
                aria-label="Close modal"
                onClick={() => onOpenChange(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-md, 10px)',
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--text-muted, #6b7280)',
                  flexShrink: 0,
                  padding: 0,
                }}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}

        {/* Body — scrollable */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {children}
        </div>

        {/* Footer — sticky at bottom */}
        {footer && (
          <div
            style={{
              flexShrink: 0,
              borderTop: '1px solid var(--border, #e5e7eb)',
              padding: 'var(--sp-4, 16px) var(--sp-6, 24px)',
              background: 'var(--bg-surface, #ffffff)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
